import type {
  PurchaseOrder,
  PurchaseOrderFormData,
  PurchaseOrderFilters,
  PurchaseOrderResponse,
  SinglePurchaseOrderResponse,
  CreatePurchaseOrderResponse,
  Supplier,
  PurchaseOrderItem
} from '@/types/purchase-order.types';
import { authFetch } from './api.service';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

// Helper para manejar respuestas de la API
const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const purchaseOrderService = {
  // Obtener lista de órdenes de compra
  async getPurchaseOrders(
    locationId: string,
    filters: Partial<PurchaseOrderFilters> = {},
    limit = 20,
    offset = 0
  ): Promise<PurchaseOrderResponse> {
    const queryParams = new URLSearchParams();

    if (filters.status && filters.status !== 'all') queryParams.append('status', filters.status);
    if (filters.supplier) queryParams.append('supplier', filters.supplier);
    if (filters.location && filters.location !== 'all') queryParams.append('location', filters.location);
    if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

    queryParams.append('limit', limit.toString());
    queryParams.append('offset', offset.toString());

    const response = await authFetch(
      `${API_BASE_URL}/locations/${locationId}/purchase-orders?${queryParams}`
    );

    return handleResponse<PurchaseOrderResponse>(response);
  },

  // Obtener lista global de órdenes de compra (todas las ubicaciones)
  async getAllPurchaseOrders(
    filters: Partial<PurchaseOrderFilters> = {},
    limit = 20,
    offset = 0
  ): Promise<PurchaseOrderResponse> {
    const queryParams = new URLSearchParams();

    if (filters.status && filters.status !== 'all') queryParams.append('status', filters.status);
    if (filters.supplier) queryParams.append('supplier', filters.supplier);
    if (filters.location && filters.location !== 'all') queryParams.append('location', filters.location);
    if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

    queryParams.append('limit', limit.toString());
    queryParams.append('offset', offset.toString());

    const response = await authFetch(
      `${API_BASE_URL}/purchase-orders?${queryParams}`
    );

    return handleResponse<PurchaseOrderResponse>(response);
  },

  // Obtener una orden específica
  async getPurchaseOrder(locationId: string, orderId: string): Promise<SinglePurchaseOrderResponse> {
    const response = await authFetch(
      `${API_BASE_URL}/locations/${locationId}/purchase-orders/${orderId}`
    );

    return handleResponse<SinglePurchaseOrderResponse>(response);
  },

  // Crear nueva orden de compra
  async createPurchaseOrder(
    locationId: string,
    orderData: PurchaseOrderFormData
  ): Promise<CreatePurchaseOrderResponse> {
    const response = await authFetch(
      `${API_BASE_URL}/locations/${locationId}/purchase-orders`,
      {
        method: 'POST',
        body: JSON.stringify(orderData),
      }
    );

    return handleResponse<CreatePurchaseOrderResponse>(response);
  },

  // Aprobar orden de compra
  async approvePurchaseOrder(
    locationId: string,
    orderId: string,
    notes?: string
  ): Promise<{ success: boolean; data: PurchaseOrder; message: string }> {
    const response = await authFetch(
      `${API_BASE_URL}/locations/${locationId}/purchase-orders/${orderId}/approve`,
      {
        method: 'PUT',
        body: JSON.stringify({ notes }),
      }
    );

    return handleResponse(response);
  },

  // Marcar orden como recibida
  async receivePurchaseOrder(
    locationId: string,
    orderId: string,
    receivedItems?: PurchaseOrderItem[],
    notes?: string
  ): Promise<{ success: boolean; data: PurchaseOrder; message: string }> {
    const response = await authFetch(
      `${API_BASE_URL}/locations/${locationId}/purchase-orders/${orderId}/receive`,
      {
        method: 'PUT',
        body: JSON.stringify({ receivedItems, notes }),
      }
    );

    return handleResponse(response);
  },

  // Cancelar orden de compra
  async cancelPurchaseOrder(
    locationId: string,
    orderId: string,
    reason?: string
  ): Promise<{ success: boolean; data: PurchaseOrder; message: string }> {
    const response = await authFetch(
      `${API_BASE_URL}/locations/${locationId}/purchase-orders/${orderId}/cancel`,
      {
        method: 'PUT',
        body: JSON.stringify({ reason }),
      }
    );

    return handleResponse(response);
  },

  // Obtener lista de proveedores
  async getSuppliers(locationId: string): Promise<{ success: boolean; data: Supplier[] }> {
    const response = await authFetch(
      `${API_BASE_URL}/locations/${locationId}/purchase-orders/suppliers/list`
    );

    return handleResponse(response);
  },

  // Crear múltiples órdenes de compra agrupadas por ubicación
  async createPurchaseOrdersByLocations(
    items: Array<{
      locationId: string;
      locationName: string;
      supplierId: string;
      supplierName: string;
      ingredientId: string;
      ingredientName: string;
      quantity: number;
      unit: string;
      unitCost: number;
      notes?: string;
    }>,
    expectedDeliveryDate?: string,
    notes?: string
  ): Promise<{
    success: boolean;
    data: {
      orders: PurchaseOrder[];
      totalOrders: number;
      errors?: string[];
    };
    message: string;
  }> {
    const response = await authFetch(
      `${API_BASE_URL}/purchase-orders/create-by-locations`,
      {
        method: 'POST',
        body: JSON.stringify({
          items,
          expectedDeliveryDate,
          notes
        }),
      }
    );

    return handleResponse(response);
  },

  // Update purchase order items and totals
  async updatePurchaseOrder(
    locationId: string,
    orderId: string,
    data: {
      items?: Array<{
        id: string;
        quantity: number;
        unitCost: number;
        totalCost: number;
      }>;
      subtotal?: number;
      tax?: number;
      total?: number;
      notes?: string;
    }
  ): Promise<{ success: boolean; data: PurchaseOrder; message: string }> {
    const response = await authFetch(
      `${API_BASE_URL}/locations/${locationId}/purchase-orders/${orderId}/update`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );

    return handleResponse(response);
  },
};

export default purchaseOrderService;